import {
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  Injector,
  HostListener,
  InjectionToken,
  Type,
  AbstractType,
} from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import {
  get,
  isFunction,
  find,
  isEmpty,
  isPlainObject,
  head,
  has,
  invoke,
  partition,
  isEqual,
  includes,
} from 'lodash';
import {
  rx,
  Observable,
  Subscription,
  observableCombineLatest,
  observableOf,
  BehaviorSubject,
} from '@shared/rx';
import { UiActions } from '@store/actions';
import {
  IModalDialogOptions,
  ToolbarButtonDefinition,
  SchemaNode,
  modalResult,
  IWidgetAction,
  XUX_FORM_ACTIONS,
  IFormAction,
  IBaseFormService,
  ENTITY_TYPES,
  SyncOrAsync,
  TOOLBAR_BUTTON_POSITION,
  ModalDynamicOptionValue,
  IWidgetEvent,
  AnyObject,
  IInlineLabelOptions,
} from '@shared/types';
import {
  ValidationService,
  IFormGroupPreferences,
  createFormGroupFromSchema,
  createFormDefinitionsFromSchema,
} from '@components/form/services';
import {
  StateService,
  BaseFormService,
  ScanningService,
} from '@shared/services';
import { XVIError } from '@shared/models/xvi-error.model';
import { asObservable, judgeScanned } from '@shared/utils';
import { KEY_CODES, WIDGET_FAMILY } from '@config/global-constants';
import { applyDynamicButtonProperties } from '@components/form/services/schema-form/schema-form-functions';
import { getEntityFormServiceClassByEntityType } from '@shared/utils/service-utils';
import { Subject } from 'rxjs';
import { select } from '@angular-redux/store';
import { BUTTON_ACTIONS } from '@app/text-content/button-templates';
import { lazyInject } from '@app/containers/base/decorators';
import { XVII18nService } from '@app/i18n/services';

const POSITIVE_MODAL_RESULTS: string[] = ['ok', 'submit', 'save'];

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xvi-form-modal',
  templateUrl: 'form-modal.component.html',
  styleUrls: [
    'form-modal.component.scss',
    'form-modal-variations.component.scss',
  ],
  encapsulation: ViewEncapsulation.None,
})
export class XVIFormModalComponent implements OnInit, OnDestroy {
  @lazyInject(ScanningService)
  public scanningService: ScanningService;

  @lazyInject(XVII18nService)
  public i18nService: XVII18nService;

  public errorObs: Observable<XVIError> = observableOf(null);

  public schemaSubscription: Subscription;

  // modal options
  public modalId: string | number = null;
  public options: IModalDialogOptions = {};

  public isScrollable: boolean = true;

  // form configuration
  public defaultWidgetFamily: string;
  public get form(): FormGroup | undefined {
    return this.form$.getValue();
  }
  public groups: IFormGroupPreferences[] = [];
  public initialDocument: object = {};

  // buttons configuration
  public footerButtons: ToolbarButtonDefinition[] = [];
  public headerButtons: ToolbarButtonDefinition[] = [];

  public closeButtonInTitle: ToolbarButtonDefinition = null;
  public groupIndexControl: FormControl = new FormControl(0);
  public formService: IBaseFormService<any>;
  public entityType: ENTITY_TYPES;
  public hideSidebar: boolean = false;
  public setupEmitWidgetEventMethod: Function;

  public modalTitle$: Observable<string | undefined>;
  public modalSubTitle$: Observable<string | undefined>;
  public autoCloseModal: boolean = true;

  public showPristineError: boolean = false;

  @select(['ui', 'modalLoader']) public isLoading$: Observable<boolean>;
  public showLoader: boolean;
  public needEventListener: boolean = false;

  public inlineLabelObs: Observable<IInlineLabelOptions[]>;

  @HostListener('window:keydown', ['$event'])
  public onClick($event: KeyboardEvent): void {
    if (!this.needEventListener) {
      return;
    }
    this.scanningService.keyTimes.push(Date.now());
    if (
      includes([KEY_CODES.TAB, KEY_CODES.ENTER], $event.key) &&
      judgeScanned(this.scanningService.keyTimes)
    ) {
      $event.preventDefault();
    }
  }

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  private formSubscriptions: Subscription[] = [];
  private originalModalButtons: ToolbarButtonDefinition[] = [];

  private form$: BehaviorSubject<FormGroup | undefined> = new BehaviorSubject(
    undefined,
  );
  /** Notice: keep in private and use {@link emitWidgetEvent} method to dispatch events */
  private _widgetsEventBus$: Subject<IWidgetEvent> =
    new Subject<IWidgetEvent>();

  public constructor(
    public uiActions: UiActions,
    public stateService: StateService,
    public injector: Injector,
  ) {}

  public get hasHeaderButtons(): boolean {
    return !!this.headerButtons.length;
  }

  public ngOnInit(): void {
    this.options = this.i18nService.translateTextInModalDialogOptions(
      this.options,
    );
    this.entityType = get(this.options, 'entityType');
    this.hideSidebar = get(this.options, 'hideSidebar', this.hideSidebar);
    this.setupEmitWidgetEventMethod = get(
      this.options,
      'setupEmitWidgetEvent',
      this.setupEmitWidgetEventMethod,
    );
    this.formService = this.injector.get(
      getEntityFormServiceClassByEntityType(this.entityType) || BaseFormService,
    );
    this.setupEmitWidgetEvent();
    this.modalId = get(this.options, 'modalId', null);
    this.defaultWidgetFamily = get(
      this.options,
      'defaultWidgetFamily',
      WIDGET_FAMILY.LEGACY,
    );
    this.isScrollable = get(this.options, 'isScrollable', true);
    this.showLoader = get(this.options, 'showLoader', false);
    this.autoCloseModal = get(this.options, 'autoCloseModal', true);
    this.showPristineError = get(
      this.options,
      'showPristineError',
      this.showPristineError,
    );
    this.inlineLabelObs = get(
      this.options,
      'inlineLabelObs',
      this.inlineLabelObs,
    );
    this.needEventListener = get(this.options, 'needEventListener', false);
    this.initialDocument = this.getInitialDocument();
    this.formService.resolveDocument(this.initialDocument);
    this.initButtons(this.options.buttons);
    this.updateSchemaSubscription(
      asObservable(get(this.options, 'schemaObs', get(this.options, 'schema'))),
    );
    this.initModalTitleStreams();
  }

  public ngOnDestroy(): void {
    this.needEventListener = false;
    this.formSubscriptions.forEach((sub) => sub.unsubscribe());
    this.formService.destroyFormDefinition();
  }

  public setupEmitWidgetEvent(): void {
    if (!this.setupEmitWidgetEventMethod) return;

    setTimeout(
      () => this.setupEmitWidgetEventMethod(this.emitWidgetEvent.bind(this)),
      0,
    );
  }

  public handleButtonClick = async (value: modalResult): Promise<void> => {
    if (
      (!this.autoCloseModal && isEqual(value, BUTTON_ACTIONS.AUTO_CLOSE)) ||
      isEqual(value, BUTTON_ACTIONS.RESET)
    ) {
      return;
    }
    const confirmed: boolean = POSITIVE_MODAL_RESULTS.includes(value);

    // check the form validity if modal has been confirmed and the form is present
    if (confirmed && this.options.validation && this.form && !this.form.valid) {
      return;
    }

    let modalValue = this.form && this.form.value;

    const modalResultMapper = get(this.options, 'modalResultMapper');

    if (isFunction(modalResultMapper)) {
      modalValue = modalResultMapper(modalValue);
    }

    if (isFunction(this.options.beforeFormClose)) {
      const closeConfirmed = await this.options.beforeFormClose(
        confirmed,
        modalValue,
        this.form,
      );
      if (!closeConfirmed) return;
    }

    this.uiActions.closeFormModalDialog({
      confirmed,
      modalResult: value,
      modalId: this.modalId,
      value: modalValue,
    });

    if (isFunction(this.options.onFormClose)) {
      setTimeout(() => this.options.onFormClose(confirmed, modalValue), 0);
    }
  };

  public hideSidebarCondition(): boolean {
    // hide sidebar in a case when is only one group is presented or no one group is present.
    return this.hideSidebar || this.groups.length <= 1;
  }

  public beforeFormGroupChange = async (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    groupIndex: number,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    group: IFormGroupPreferences,
  ): Promise<boolean> => {
    return true;
  };

  public initButtons(buttonsFromOptions: ToolbarButtonDefinition[] = []): void {
    this.originalModalButtons = buttonsFromOptions;
  }

  public onFormAction = async (formAction: IFormAction): Promise<void> => {
    invoke(this.options, 'onFormAction', formAction);
    this.refreshButtons(formAction);
  };

  protected emitWidgetEvent<T extends AnyObject>(
    widgetFieldPath: string,
    eventName: string,
    data: T,
  ): void {
    this._widgetsEventBus$.next({ widgetFieldPath, eventName, data });
  }

  private updateSchemaSubscription(schemaObs: Observable<SchemaNode>): void {
    if (this.schemaSubscription) {
      this.schemaSubscription.unsubscribe();
    }
    this.schemaSubscription = schemaObs
      .pipe(
        rx.map((schema = {}) => {
          // map schema before form initialization
          if (isFunction(this.options.schemaMapper)) {
            return this.options.schemaMapper.call(this, schema);
          }

          return schema;
        }),
      )
      .subscribe((schema) => {
        this.initForm(schema);
      });
  }

  private formValueSubscription(): void {
    this.form.valueChanges.subscribe((value) => {
      this.uiActions.storeValueByModalId(this.modalId, value);
    });
  }

  private getInitialDocument(): object {
    const valueFromOptions = get(this.options, 'value', null);

    if (isFunction(valueFromOptions)) {
      return valueFromOptions();
    }

    if (isPlainObject(valueFromOptions) && !isEmpty(valueFromOptions)) {
      return valueFromOptions;
    }

    return {};
  }

  private initForm(schema: SchemaNode): void {
    const formRules = this.options.formRules;
    const form = createFormGroupFromSchema({
      schema,
      rules: formRules,
      i18nService: this.i18nService,
    });
    this.formService.resolveForm(form);
    this.form$.next(form);
    if (!this.options.validation) {
      // TODO: turn off validation for deeply nested controls
      Object.values(this.form.controls || {}).forEach((control) =>
        control.clearValidators(),
      );
    } else {
      this.errorObs = observableCombineLatest([
        // eslint-disable-next-line import/no-deprecated
        this.form.statusChanges.pipe(rx.startWith(null)),
        // eslint-disable-next-line import/no-deprecated
        this.form.valueChanges.pipe(rx.startWith(null)),
      ]).pipe(
        rx.throttleTime(250, undefined, { leading: true, trailing: true }),
        rx.map(() =>
          this.form.valid
            ? null
            : ValidationService.findFormError(this.form, formRules, {
                i18nService: this.i18nService,
              }),
        ),
        rx.distinctUntilChanged(isEqual),
      );

      this.originalModalButtons = this.originalModalButtons.map((button) =>
        POSITIVE_MODAL_RESULTS.includes(button.value) &&
        !has(button, 'disabled')
          ? {
              ...button,
              disabled: (): boolean =>
                get(this.form, 'invalid', false) ||
                get(this.form, 'pending', false),
            }
          : button,
      );
    }

    if (!isEmpty(this.initialDocument)) {
      this.form.patchValue(this.initialDocument);
    }

    this.groups = createFormDefinitionsFromSchema({
      schema,
      rules: this.options.formRules,
      form: this.form,
      eventBus$: this._widgetsEventBus$.asObservable(),
      i18nService: this.i18nService,
    });

    if (isEmpty(get(this.options, ['formRules', 'groups']))) {
      this.groups.forEach((group) => (group.name = ''));
    }

    if (isFunction(this.options.onFormReady)) {
      setTimeout(() => this.options.onFormReady(this.form, this.groups), 0);
    }

    head(this.groups).action.next({ type: XUX_FORM_ACTIONS.FORM_READY });
    this.formValueSubscription();
  }

  private refreshButtons(formAction?: IWidgetAction): void {
    // calculate and apply DynamicProperties
    const staticButtons = this.applyDynamicProperties(
      this.originalModalButtons,
      get(this.form, 'value'),
      formAction,
    );
    // split buttons (currently the only group exists)
    this.setVisibleButtons(staticButtons);
  }

  // Incoming buttons should already have dynamic properties applied
  private setVisibleButtons(buttons: ToolbarButtonDefinition[]): void {
    const translatedButtons = this.i18nService.translateButtonsText(buttons);

    this.separateButtons(translatedButtons);
    this.closeButtonInTitle = find(this.footerButtons, {
      value: 'cancel',
    } as any);
  }

  private separateButtons(buttons: ToolbarButtonDefinition[]): void {
    [this.headerButtons, this.footerButtons] = partition(
      buttons,
      (button) => button.position === TOOLBAR_BUTTON_POSITION.HEADER,
    );
  }

  /**
   * Returns an array of new buttons where all dynamic properties are calculated and applied
   */
  private applyDynamicProperties(
    originalButtons: ToolbarButtonDefinition[],
    formValue: any,
    formAction?: IWidgetAction,
  ): ToolbarButtonDefinition[] {
    return applyDynamicButtonProperties(originalButtons, formValue, formAction);
  }

  private initModalTitleStreams(): void {
    const title: IModalDialogOptions['title'] = get(this.options, 'title', '');
    const subTitle: IModalDialogOptions['subTitle'] = get(
      this.options,
      'subTitle',
      '',
    );

    this.modalTitle$ = get(
      this.options,
      'titleObs',
      this.titleAsObservable(title),
    );
    this.modalSubTitle$ = get(
      this.options,
      'subTitleObs',
      this.titleAsObservable(subTitle),
    );
  }

  private titleAsObservable(
    title: ModalDynamicOptionValue,
  ): Observable<string> {
    const fnToStream = (
      fn: (formValue: object) => string,
    ): Observable<string> =>
      this.form$.pipe(
        rx.filter<FormGroup>(Boolean),
        rx.map((form) => fn(form.value)),
      );

    const titleAsObservable = (
      titleLocal: SyncOrAsync<string>,
    ): Observable<string> =>
      asObservable(titleLocal).pipe(
        rx.map((titleStr?: string | null) => titleStr || undefined),
      );

    return isFunction(title)
      ? titleAsObservable(fnToStream(title))
      : titleAsObservable(title);
  }
}
